import React, { useCallback, useRef, useState } from "react";
import { CellContext } from "@tanstack/react-table";
import { Dropdown } from "react-bootstrap";
import { TransactionListItemDto } from "../../../common/types/transaction";
import { FinancialDocument } from "../../../common/types/financialDocument";
import { toBase64 } from "../../../helpers/file";
import { uploadTransactionReceipt } from "../../../lib/transactions";
import { UPLOAD_RECEIPT_ACTION } from "../../../common/requiredActions";
import { Button } from "../../general/Button/Button";
import { Loader } from "../../general/Loader";
import { ManagementDropdown } from "../../general/ManagementDropdown/ManagementDropdown";
import { DotsHorizontalIcon } from "../../../icons";
import { useUpdateTransactionMutation } from "../../../mutations/transaction";
import styles from "./ReceiptUploadCell.module.scss";

export const ReceiptUploadCell = ({
    row,
}: CellContext<TransactionListItemDto, any>) => {
    const transaction = row.original;
    const [uploading, setUploading] = useState(false);
    const [uploadedDocument, setUploadedDocument] =
        useState<FinancialDocument | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const updateTransaction = useUpdateTransactionMutation();
    const [snoozing, setSnoozing] = useState(false);
    const [isSnoozed, setIsSnoozed] = useState(false);

    const handleButtonClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleUpload = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) {
                return;
            }
            setUploading(true);
            try {
                const receipt = await toBase64(file);
                const uploadedReceipt = await uploadTransactionReceipt(
                    transaction.id,
                    {
                        receipt,
                        contentType: file.type,
                        name: file.name,
                    },
                );
                setUploadedDocument(uploadedReceipt);
            } finally {
                setUploading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }
        },
        [transaction.id],
    );

    const onSnoozeRequirements = useCallback(async () => {
        setSnoozing(true);
        try {
            await updateTransaction.mutateAsync({
                transaction,
                update: { requirementsSnoozed: true },
            });
            setIsSnoozed(true);
        } finally {
            setSnoozing(false);
        }
    }, [transaction, updateTransaction]);

    if (uploading || snoozing) {
        return (
            <Button variant="secondary" disabled className={styles.button}>
                <Loader inline />
            </Button>
        );
    }

    if (isSnoozed) {
        return (
            <Button variant="secondary" disabled className={styles.button}>
                Snoozed
            </Button>
        );
    }

    if (
        uploadedDocument ||
        !transaction.requiredActions?.includes(UPLOAD_RECEIPT_ACTION)
    ) {
        return (
            <Button variant="secondary" disabled className={styles.button}>
                Uploaded
            </Button>
        );
    }

    return (
        <div className={styles.container}>
            <Button
                variant="secondary"
                onClick={handleButtonClick}
                className={styles.button}
            >
                Upload
            </Button>
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/png,image/jpeg,application/pdf"
                onChange={handleUpload}
            />
            <ManagementDropdown
                id={`transaction-dropdown-${transaction.id}`}
                trigger={<DotsHorizontalIcon />}
                popperConfigStrategy="fixed"
            >
                <Dropdown.Item
                    as="button"
                    onClick={onSnoozeRequirements}
                    disabled={snoozing}
                >
                    <div className={styles.dropdownItem}>
                        <header>Snooze requirements</header>
                        <span>
                            This means you may not have the substantiation
                            required by the IRS during an audit.
                        </span>
                    </div>
                </Dropdown.Item>
            </ManagementDropdown>
        </div>
    );
};
